<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ArabicText from '@/components/quran/ArabicText.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiSkeleton from '@/components/ui/UiSkeleton.vue';
import { useAsyncData } from '@/composables/useAsyncData';
import { useQuranPlayer } from '@/composables/quran/useQuranPlayer';
import { fetchSurah } from '@/utils/quran/api';
import { ayahPath } from '@/utils/quran/paths';

/**
 * One ayah a day, the same for everyone that day — from a short list of
 * ayat people know and return to. Plays in the shared player.
 */
const DAILY = [
  [2, 255], [2, 286], [2, 152], [2, 186], [3, 139], [3, 173], [13, 28], [94, 5], [94, 6], [65, 3],
  [39, 53], [2, 153], [29, 69], [20, 114], [17, 80], [40, 60], [8, 46], [3, 159], [49, 13], [55, 13],
  [16, 97], [18, 46], [21, 87], [25, 63], [31, 18], [33, 41], [57, 4], [59, 22], [67, 2], [112, 1],
];

const { t, locale } = useI18n();
const player = useQuranPlayer();

const day = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86_400_000);
const [surahNumber, ayahNumber] = DAILY[day % DAILY.length];

const { data: surah } = useAsyncData(() => surahNumber, fetchSurah);
const ayah = computed(() => surah.value?.ayahs[ayahNumber - 1] ?? null);

const isPlaying = computed(() => player.isCurrent(surahNumber, ayahNumber) && player.state.isPlaying);

function play() {
  if (player.isCurrent(surahNumber, ayahNumber)) return player.toggle();
  player.play([{ ...ayah.value, surahName: surah.value.transliteration }]);
}
</script>

<template>
  <article class="relative w-full max-w-lg rounded-[32px] bg-white border border-deen-ink/5 shadow-[0_30px_80px_-30px_rgba(28,72,72,0.35)] p-7 overflow-hidden">
    <div class="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-deen-gold/25 blur-2xl pointer-events-none" aria-hidden="true" />
    <header class="relative flex items-center justify-between mb-4">
      <p class="text-xs font-bold uppercase tracking-widest text-deen-ink/40">{{ t('quran.landing.daily') }}</p>
      <router-link v-if="ayah" :to="ayahPath(surahNumber, ayahNumber)" class="text-sm font-semibold text-deen-ink hover:underline underline-offset-4">
        {{ surah.transliteration }} {{ ayah.key }}
      </router-link>
    </header>

    <template v-if="ayah">
      <ArabicText :text="ayah.arabic" :size="ayah.arabic.length > 160 ? 1 : 2" class="relative" />
      <p class="relative mt-4 font-reading text-[17px] text-deen-ink/80 leading-[1.75] line-clamp-5">{{ ayah.translation[locale] ?? ayah.translation.en }}</p>
      <div class="relative flex flex-wrap gap-2 mt-6">
        <UiButton intent="primary" :icon="isPlaying ? 'solar:pause-bold' : 'solar:play-bold'" @click="play">
          {{ isPlaying ? t('quran.player.pause') : t('quran.landing.listen') }}
        </UiButton>
        <UiButton intent="secondary" icon="solar:square-academic-cap-linear" :to="ayahPath(surahNumber, ayahNumber)">
          {{ t('quran.landing.study') }}
        </UiButton>
      </div>
    </template>
    <UiSkeleton v-else :lines="4" height="h-7" />
  </article>
</template>
